/**
 * AdminFunnelTab - Detailed funnel view with step-by-step conversion rates.
 * Supports channel filter and period comparison.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getDashboardFunnel,
  getDashboardChannels,
  type FunnelStats,
  type ChannelRow,
} from '@/services/adminDashboardService';

const PERIODS = [
  { value: '1d', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

const STEP_LABELS: Record<string, string> = {
  account_created: 'Conta criada',
  login_completed: 'Login realizado',
  onboarding_started: 'Onboarding iniciado',
  onboarding_completed: 'Onboarding completo',
  carousel_created: 'Carrossel criado',
  checkout_initiated: 'Checkout iniciado',
  checkout_form_started: 'Form iniciado',
  checkout_form_completed: 'Form completo',
  payment_approved: 'Pagamento aprovado',
  payment_failed: 'Pagamento falhado',
};

const STEP_ORDER = [
  'account_created',
  'onboarding_started',
  'onboarding_completed',
  'carousel_created',
  'checkout_initiated',
  'payment_approved',
  'payment_failed',
] as const satisfies readonly (keyof FunnelStats)[];

const AdminFunnelTab: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [channel, setChannel] = useState('');
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [funnelData, channelsData] = await Promise.all([
        getDashboardFunnel(period, channel || undefined),
        getDashboardChannels(period),
      ]);
      setStats(funnelData);
      setChannels(channelsData.channels);
    } catch (err) {
      console.error('Failed to fetch funnel data', err);
    } finally {
      setLoading(false);
    }
  }, [period, channel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading || !stats) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  const totalAccounts = stats.account_created || 1;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Channel filter */}
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
        >
          <option value="">Todos os canais</option>
          {channels.map((ch) => (
            <option key={ch.channel} value={ch.channel}>{ch.channel}</option>
          ))}
        </select>
      </div>

      {/* Funnel table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Etapa</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Volume</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">% vs Anterior</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">% vs Total</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Drop-off</th>
                <th className="px-4 py-3 font-medium text-muted-foreground">Barra</th>
              </tr>
            </thead>
            <tbody>
              {STEP_ORDER.map((key, i) => {
                const value = stats[key];
                const prev = i > 0 ? stats[STEP_ORDER[i - 1]] : value;
                const vsPrev = prev > 0 ? ((value / prev) * 100).toFixed(1) : '—';
                const vsTotal = ((value / totalAccounts) * 100).toFixed(1);
                const dropoff = prev > 0 ? prev - value : 0;
                const dropoffPct = prev > 0 ? ((dropoff / prev) * 100).toFixed(1) : '0';
                const barPct = (value / totalAccounts) * 100;

                // payment_failed is special — show it but don't compute dropoff from previous
                const isFailStep = key === 'payment_failed';

                return (
                  <tr key={key} className={`border-t border-border ${isFailStep ? 'bg-destructive/5' : ''}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{STEP_LABELS[key]}</td>
                    <td className="text-right px-4 py-3 text-foreground font-mono">{value}</td>
                    <td className="text-right px-4 py-3 text-foreground">
                      {i > 0 && !isFailStep ? `${vsPrev}%` : '—'}
                    </td>
                    <td className="text-right px-4 py-3 text-foreground">{vsTotal}%</td>
                    <td className="text-right px-4 py-3">
                      {i > 0 && dropoff > 0 && !isFailStep ? (
                        <span className="text-destructive">-{dropoff} ({dropoffPct}%)</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="w-full bg-muted rounded-full h-3">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${isFailStep ? 'bg-destructive' : 'bg-primary'}`}
                          style={{ width: `${Math.max(barPct, 1)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminFunnelTab;
